import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Grid,
  IconButton,
  Tooltip,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import { MovableCardWrapper } from '../common/board/styles/Base';
import contentEntryPlaceholder from '../../assets/images/content_entry_placeholder.png';
import { parentIDOfSharedContentEntryID } from '../../services/space_content_board';
import {
  useGetOrCreateSpaceContentBoardsQuery,
  useGetSharedContentEntryByIDAndParentQuery,
} from '../../app/smorgAppSyncGraphQLApiCustomHooks';
import { programmePlanEntryQuickDuplicate } from '../../operations/programmes_operations';
import {
  useCreateContentEntryMutation,
  useGetProgrammesQuery,
  useGetSpaceQuery,
  useLazyGetContentEntryQuery,
  useUpdateProgrammeMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { planEntryByEntryID } from '../../services/programmes';
import { healthProGroupForSpaceID } from '../../services/users';
import { ActivityObjectType } from '../../API';
import { reportAccountActivitySignalOperation } from '../../operations/user_profile_operations';
import { userFriendlyNameForActivitySignalSelector } from '../../features/user_id/user_id_slice';

const contentEntryChipStyle = {
  color: 'white',
  backgroundColor: '#FF0000',
  borderColor: '#FF0000',
};

const ProgrammeEntrySharedContentEntryCard = ({
  id,
  appBoardId: programmePlanId,
  title,
  style,
  className,
  onClick,
  onDelete,
  // isPreview,
}) => {
  const sharedContentEntryID = style._objectID;

  console.log(
    `ProgrammeEntrySharedContentEntryCard sharedContentEntryID: ${sharedContentEntryID}`,
  );

  const { data: ownedProgrammes } = useGetProgrammesQuery();

  const programme = (ownedProgrammes || []).find((pr) =>
    pr.plans.some((pl) => pl.id === programmePlanId),
  );

  const programmePlan = programme.plans.find((pl) => pl.id === programmePlanId);

  const entry = planEntryByEntryID(programmePlan, id);

  const { data: sharedContentBoards } = useGetOrCreateSpaceContentBoardsQuery();

  const parentID = parentIDOfSharedContentEntryID(
    sharedContentEntryID,
    sharedContentBoards,
  );

  const { data: sharedContentEntry } =
    useGetSharedContentEntryByIDAndParentQuery(
      { id: sharedContentEntryID, parentID },
      { skip: !sharedContentEntryID || !parentID },
    );

  const [lazyGetContentEntry] = useLazyGetContentEntryQuery();

  const [createContentEntry] = useCreateContentEntryMutation();

  const { data: healthProSpace } = useGetSpaceQuery();

  const currentHealthProGroup =
    healthProSpace && healthProGroupForSpaceID(healthProSpace.id);

  const [updateProgramme] = useUpdateProgrammeMutation();

  const friendlyUsername = useSelector(
    userFriendlyNameForActivitySignalSelector,
  );

  const onQuickDuplicate = async () => {
    reportAccountActivitySignalOperation({
      spaceID: healthProSpace.id,
      friendlyUsername,
      lastActivityObjectType: ActivityObjectType.PROGRAMMES,
      lastActivityObjectID: programme.id,
    });
    return programmePlanEntryQuickDuplicate(
      programme,
      programmePlanId,
      entry,
      currentHealthProGroup,
      updateProgramme,
      lazyGetContentEntry,
      createContentEntry,
    );
  };

  if (!sharedContentEntry) {
    return null;
  }

  const isPreview = false;

  return (
    <MovableCardWrapper
      data-id={id}
      onClick={() => {
        return !isPreview && onClick(id);
      }}
      style={style}
      className={className}>
      <Card sx={{ display: 'flex', flexDirection: 'column' }}>
        <Grid item xs={12}>
          <CardMedia
            component="img"
            className="contentEntryCardImage"
            image={sharedContentEntry?.coverImageUrl || contentEntryPlaceholder}
            alt=""
          />
        </Grid>
        <Grid item xs={12}>
          <CardContent
            sx={{
              flex: '1 0 auto',
              margin: '8px',
              padding: '0 !important',
              fontSize: '14px',
              whiteSpace: 'break-spaces',
            }}
            style={{
              maxWidth: 156,
              lineClamp: 3,
              WebkitLineClamp: 3,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitBoxOrient: 'vertical',
            }}>
            {title}
          </CardContent>
        </Grid>
        <Grid item xs={12} sx={{ margin: '2px 0 4px' }}>
          <Grid container>
            <Grid item xs={4} sx={{ display: 'flex' }}>
              <div style={{ margin: 'auto 8px' }}>
                <Chip
                  label="LESSON"
                  className="meal-type-chip"
                  variant="outlined"
                  size="small"
                  style={contentEntryChipStyle}
                />
              </div>
            </Grid>
            <Grid item xs={8}>
              {!isPreview && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    padding: '4px',
                  }}>
                  <Tooltip title="Duplicate this lesson" placement="top-start">
                    <IconButton
                      aria-label="quick duplicate"
                      size="small"
                      onClick={(ev) => {
                        onQuickDuplicate();
                        ev.stopPropagation();
                      }}>
                      <FileCopyIcon />
                    </IconButton>
                  </Tooltip>
                  <IconButton
                    aria-label="delete"
                    size="small"
                    onClick={(ev) => {
                      onDelete(id);
                      ev.stopPropagation();
                    }}>
                    <DeleteIcon />
                  </IconButton>
                </Box>
              )}
            </Grid>
          </Grid>
        </Grid>
      </Card>
    </MovableCardWrapper>
  );
};

ProgrammeEntrySharedContentEntryCard.propTypes = {
  id: PropTypes.string.isRequired,
  appBoardId: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object.isRequired,
  className: PropTypes.string.isRequired,
  onClick: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  // isPreview: PropTypes.bool,
};

ProgrammeEntrySharedContentEntryCard.defaultProps = {
  // isPreview: false,
};

export default ProgrammeEntrySharedContentEntryCard;
